import { Box } from "@mui/material";
import { StyledLink, StyledSpan, StyledTableCell } from "../Styled";
import CachedImage from "../../../../../../common/CachedImage";
import moment from "moment";
import { normalizeTimezone } from "../../../../../../../utils/moment";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import CheckboxInput from "../../../../../FMForm/FormTypes/CheckboxInput/CheckboxInput";
import DOMPurify from 'dompurify';

export const RenderCellContent = ({
    row,
    column,
    options,
    userDomain,
    userTimezone,
    defaultUserTimezone,
    downloadFile = () => { },
    handleAction = () => { },
    handleOnAction = () => { },
    showCreateConversation = () => { },
}) => {

    const theme = useTheme();
    const largeScreen = useMediaQuery(theme.breakpoints.up("sm"));
    const normalizedTz = normalizeTimezone(userTimezone || defaultUserTimezone);
    // if (!column || column.hidden) {
    //     return;
    // };

    switch (column.type) {
        case "text_field":
        case "text_area":
        case "radiobutton":
        case "dropdown":
        case "multi_selection":
        case "checkbox":
        case "slider":
        case "number":
        case "lookup":
            const isLookup = column.type === "lookup" && Object.prototype.toString.call(column.value) === "[object Object]";
            const allowQuickAction = !Object.hasOwnProperty.call(options, "allowQuickAction") || options.allowQuickAction;

            if (allowQuickAction) {
                if (column.primaryKey || column.quickView) {
                    return (
                        <StyledLink
                            onClick={() => {
                                const docId = Object.keys(row)[0] || null
                                const objValue = Object.values(row)[0].fields.reduce((acc, col) => {
                                    acc[col.id] = col.value;
                                    return acc;
                                }, {});
                                const isDisableRefresh = true
                                handleAction("quickAction", objValue, null, docId, null, isDisableRefresh);
                            }}
                        >
                            {isLookup ? column.value.text : column.value}
                        </StyledLink>
                    );
                }

                if (isLookup) {
                    return column.value.text
                }

                if (column.type === "multi_selection") {
                    return column.value.join(",  ");
                }

                return column.value;
            }
            return isLookup ? column.value.text : column.value;
        case "rich_text_area":
            return column.value ? (
                <Box
                    component="div"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(column.value || '') }}
                />
            ) : null;
        case "switch":
            return column.value ? "Enabled" : "Disabled";
        case "phone_number":
            return (
                <StyledLink
                    onClick={() => {
                        Object.prototype.toString.call(column.value) === "[object Object]"
                            ? showCreateConversation(column.value.text)
                            : showCreateConversation(column.value);
                    }}
                    userDomain={userDomain}
                >
                    {column.type === "lookup" &&
                        Object.prototype.toString.call(column.value) === "[object Object]"
                        ? column.value.text
                        : column.value}
                </StyledLink>
            );
        case "email_field":
            const emailValue =
                column.type === "lookup" &&
                    Object.prototype.toString.call(column.value) === "[object Object]"
                    ? column.value.text
                    : column.value;
            return (
                <StyledLink
                    userDomain={userDomain}
                    onClick={() => {
                        if (emailValue) {
                            window.open(`mailto:${emailValue}`, "_blank");
                        }
                    }}
                >
                    {emailValue}
                </StyledLink>
            );
        case "password_field":
            return "*****";
        case "buttons_field":
            // Handle icon-based rendering
            if (column.activeIconUrl && column.inactiveIconUrl) {
                if (!column.activeIconUrl.length) return null;

                return (
                    <a onClick={!column.readOnly ? () => handleOnAction("onFieldAction", column.activeIconUrl, column) : undefined}>
                        <img
                            src={column.readOnly ? column.inactiveIconUrl : column.activeIconUrl}
                            alt=""
                            height="40px"
                        />
                    </a>
                );
            };

            // Handle options list rendering
            if (column.options && column.options.length > 0) {
                return (
                    <>
                        {column.options.map((item) => (
                            <a key={item.label} onClick={() => column.readOnly ? null : handleOnAction("onFieldAction", item.label, column)}>
                                <span
                                    className={`${userDomain}_font`}
                                    style={{
                                        color: column.readOnly ? "rgb(153, 153, 153)" : "rgb(0, 189, 242)",
                                        textDecoration: "underline",
                                        cursor: column.readOnly ? "default" : "pointer",
                                    }}
                                >
                                    {item.label}
                                </span>
                            </a>
                        ))}
                    </>
                );
            };

            // Handle default text link rendering
            const isReadOnly = column?.readOnly;
            const displayText =
                typeof column.value === "object" && column.value !== null
                    ? column.value.text
                    : column.value;

            return (
                <a onClick={!isReadOnly ? () => handleOnAction("onFieldAction", displayText, column) : undefined}>
                    <span
                        className={`${userDomain}_font`}
                        style={{
                            color: isReadOnly ? "rgb(153, 153, 153)" : "rgb(0, 189, 242)",
                            textDecoration: "underline",
                            cursor: isReadOnly ? "default" : "pointer",
                        }}
                    >
                        {displayText}
                    </span>
                </a>
            );
        case "image_field":
        case "file_field":
            return column.value ? (
                <StyledLink onClick={() => downloadFile(column)}>
                    {column.fileName}
                </StyledLink>
            ) : null;
        case "date":
            const showAsQuickAction =
                (column.primaryKey || column.quickView) &&
                (!Object.hasOwnProperty.call(options, "allowQuickAction") || column.allowQuickAction);

            const formattedDate = column.value
                ? moment(column.value)
                    .tz(normalizedTz)
                    .format("ll")
                : null;

            const dateContent = <StyledSpan>{formattedDate}</StyledSpan>;

            if (showAsQuickAction) {
                const objValue = {};
                Object.values(row)[0].fields.forEach((col) => {
                    objValue[col.id] = col.value;
                });

                return (
                    <StyledLink
                        userDomain={userDomain}
                        onClick={() => handleAction("quickAction", objValue)}
                    >
                        {dateContent}
                    </StyledLink>
                );
            }

            return dateContent;
        case "time": {
            const showAsQuickAction =
                (column.primaryKey || column.quickView) &&
                (!Object.hasOwnProperty.call(options, "allowQuickAction") || column.allowQuickAction);

            const formattedTime = column.value
                ? moment(column.value)
                    .tz(normalizedTz)
                    .format("LT")
                : null;

            if (!formattedTime) return null;

            if (showAsQuickAction) {
                const objValue = {};
                Object.values(row)[0].fields.forEach((col) => {
                    objValue[col.id] = col.value;
                });

                return (
                    <StyledLink
                        userDomain={userDomain}
                        onClick={() => handleAction("quickAction", objValue)}
                    >
                        {formattedTime}
                    </StyledLink>
                );
            }

            return formattedTime;
        }
        case "datetime": {
            // We display time with seconds only for Thuraya Doamin.
            // let dateFormat = getSignupPath() == THURAYA_LANDING ? "MMM D, YYYY h:mm:ss A" : options.title == "Manage PEME Cases" ? "MMM D, YYYY HH:mm":"lll";
            const dateFormat = options.title === "Manage PEME Cases" ? "MMM D, YYYY HH:mm" : "lll";

            const formattedDate = column.value
                ? moment(column.value)
                    .tz(normalizedTz)
                    .format(dateFormat)
                : null;

            if (!formattedDate) return null;

            const showAsQuickAction =
                (column.primaryKey || column.quickView) &&
                (!Object.hasOwnProperty.call(options, "allowQuickAction") || column.allowQuickAction);

            if (showAsQuickAction) {
                const objValue = {};
                Object.values(row)[0].fields.forEach((col) => {
                    objValue[col.id] = col.value;
                });

                return (
                    <StyledLink
                        userDomain={userDomain}
                        onClick={() => handleAction("quickAction", objValue)}
                    >
                        <StyledSpan>{formattedDate}</StyledSpan>
                    </StyledLink>
                );
            }

            return <StyledSpan>{formattedDate}</StyledSpan>;
        }
        case "alert_field":
            switch (column.value) {
                case 1:
                    return (
                        <div
                            style={{
                                boxSizing: "border-box",
                                borderRadius: "4px",
                                height: " 12px",
                                width: "12px",
                                backgroundColor: "#2DB757",
                            }}
                        ></div>
                    );
                case 2:
                    return (
                        <span>
                            <CachedImage
                                imgKey="warningIcon"
                                image="/images/warning@2x.png"
                                width="14px"
                            />
                        </span>
                    );
                case 3:
                    return (
                        <span>
                            <CachedImage
                                imgKey="errorIcon"
                                image="/images/error-icon@2x.png"
                                width="14px"
                            />
                        </span>
                    );
                default:
                    return column.value;
            }
        case "boolean_field":
            return (
                <StyledTableCell align="left">
                    <CheckboxInput
                        {...column}
                        readOnly={options?.readOnly ? true : column.readOnly}
                        isSmallScreen={!largeScreen}
                        isTable={true}
                    />
                </StyledTableCell>
            );
        case "object_multi_selection": {
            let opts = column.options;
            let vals = column.value;
            let dspfldArr = [];

            for (let i = 0; i < vals?.length; i++) {
                let selectedItm = opts?.find((opt) => opt?.value === vals[i]);
                if (selectedItm) {
                    i === vals?.length - 1
                        ? dspfldArr.push(selectedItm?.displayField)
                        : dspfldArr.push(selectedItm?.displayField + ", ");
                }
            }
            if (
                !Object.hasOwnProperty.call(options, "allowQuickAction") ||
                options?.allowQuickAction
            ) {
                return (column.primaryKey || column.quickView) ? (
                    <StyledLink
                        userDomain={userDomain}
                        onClick={() => {
                            let objValue = {};
                            Object.values(row)[0].fields.map((col) => {
                                objValue[col.id] = col.value;
                                return col;
                            });
                            handleAction("quickAction", objValue);
                        }}
                    >
                        {dspfldArr}
                    </StyledLink>
                ) : (
                    dspfldArr
                );
            }
            return dspfldArr;
        }
        case "icon_field":
            return (
                <img
                    src={column.value}
                    width="20px"
                    height="20px"
                    alt="field_icon"
                />
            );
    }
};
